"use client";

import {
  AlarmClock,
  BadgeCheck,
  BellRing,
  ListTodo,
  MessageSquare,
  UserCheck,
  Users,
  Workflow,
} from "lucide-react";
import { Conversation, type Contact, type ThreadItem } from "../_shared/conversation";
import {
  FeatureUseCases,
  type SceneProps,
  type UseCase,
  useRevealCount,
} from "../_shared/use-cases-section";

/* ------------------------------------------------------------------ */
/* 1 · A conversation becomes a task                                  */
/* ------------------------------------------------------------------ */

const CASEY: Contact = {
  name: "Casey Lin",
  src: "/images/website/customers/casey-lin.jpg",
  status: "Trial · day 3",
};

const FROM_CONVERSATION: ThreadItem[] = [
  {
    kind: "message",
    id: "c1",
    direction: "incoming",
    body: "Loved this morning's class! Still deciding between the 10-pack and the monthly though.",
  },
  {
    kind: "message",
    id: "c2",
    direction: "outgoing",
    body: "So glad! Alex knows the options inside out. She'll give you a call tomorrow morning.",
  },
  {
    kind: "event",
    id: "c3",
    icon: ListTodo,
    title: "Task created from this conversation",
    sub: "Call Casey re: membership options",
  },
  {
    kind: "event",
    id: "c4",
    icon: UserCheck,
    title: "Assigned to Alex",
    sub: "Due tomorrow · 10:00 AM",
  },
  { kind: "system", id: "c5", text: "9:00 AM · due-soon push sent to Alex" },
  { kind: "call", id: "c6", title: "Outgoing call · Alex", sub: "8m 12s · walked through options" },
  {
    kind: "event",
    id: "c7",
    icon: MessageSquare,
    title: "Alex commented",
    sub: "Went monthly, starts the 1st",
  },
  { kind: "event", id: "c8", icon: BadgeCheck, title: "Task completed", sub: "On time, on record" },
];

function FromConversationScene({ progress, reduce, flow, active }: SceneProps) {
  const visible = useRevealCount(progress, FROM_CONVERSATION.length, reduce, active);
  return (
    <Conversation
      contact={CASEY}
      items={FROM_CONVERSATION}
      visible={visible}
      reduce={reduce}
      flow={flow}
    />
  );
}

/* ------------------------------------------------------------------ */
/* 2 · A flow drops the task on the right desk                        */
/* ------------------------------------------------------------------ */

const DEVIN: Contact = {
  name: "Devin Carter",
  src: "/images/website/customers/devin-carter.jpg",
  status: "Intro offer · day 5",
};

const FROM_FLOW: ThreadItem[] = [
  {
    kind: "event",
    id: "d1",
    icon: Workflow,
    title: "Flow · Intro offer nurture",
    sub: "Day 5 step reached",
  },
  {
    kind: "event",
    id: "d2",
    icon: ListTodo,
    title: "Task created automatically",
    sub: "Check in before the intro ends · due in 2 days",
  },
  {
    kind: "event",
    id: "d3",
    icon: Users,
    title: "In Uptown's shared pool",
    sub: "Anyone at the front desk can claim it",
  },
  {
    kind: "event",
    id: "d4",
    icon: UserCheck,
    title: "Jordan claimed it",
    sub: "Front desk · Uptown",
  },
  {
    kind: "message",
    id: "d5",
    direction: "outgoing",
    body: "Hey Devin! Your intro wraps up Friday. Want me to hold the founding rate for you?",
  },
  {
    kind: "message",
    id: "d6",
    direction: "incoming",
    body: "Yes please. I'll swing by tonight to set it up.",
  },
  {
    kind: "event",
    id: "d7",
    icon: BadgeCheck,
    title: "Task done · membership started",
    sub: "Logged at Uptown",
  },
];

function FromFlowScene({ progress, reduce, flow, active }: SceneProps) {
  const visible = useRevealCount(progress, FROM_FLOW.length, reduce, active);
  return (
    <Conversation contact={DEVIN} items={FROM_FLOW} visible={visible} reduce={reduce} flow={flow} />
  );
}

/* ------------------------------------------------------------------ */
/* 3 · Snoozed, woken, and finished from the iOS app                  */
/* ------------------------------------------------------------------ */

const MARISSA: Contact = {
  name: "Marissa Larimer",
  src: "/images/website/customers/marissa-larimer.jpg",
  status: "Membership frozen",
  statusTone: "warn",
};

const FROM_PHONE: ThreadItem[] = [
  {
    kind: "event",
    id: "r1",
    icon: BellRing,
    title: "Push · You were assigned a task",
    sub: "Call Marissa about her freeze",
  },
  {
    kind: "event",
    id: "r2",
    icon: AlarmClock,
    title: "Snoozed until 3:00 PM",
    sub: "Mid-class rush, parked not forgotten",
  },
  { kind: "system", id: "r3", text: "3:00 PM · back in To do, push sent" },
  { kind: "call", id: "r4", title: "Outgoing call · from the iOS app", sub: "4m 05s" },
  {
    kind: "message",
    id: "r5",
    direction: "outgoing",
    body: "Great catching up, Marissa! You're unfrozen and back on the schedule starting Monday.",
  },
  { kind: "message", id: "r6", direction: "incoming", body: "Perfect, see you Monday!" },
  {
    kind: "event",
    id: "r7",
    icon: BadgeCheck,
    title: "Checked off from the iOS app",
    sub: "Done between classes",
  },
];

function FromPhoneScene({ progress, reduce, flow, active }: SceneProps) {
  const visible = useRevealCount(progress, FROM_PHONE.length, reduce, active);
  return (
    <Conversation
      contact={MARISSA}
      items={FROM_PHONE}
      visible={visible}
      reduce={reduce}
      flow={flow}
    />
  );
}

/* ------------------------------------------------------------------ */
/* Section                                                            */
/* ------------------------------------------------------------------ */

const CASES: UseCase[] = [
  {
    id: "from-conversation",
    title: "The promised call actually happens",
    description:
      "A question in the thread becomes a linked task in two clicks. The reminder lands before the deadline, the call gets made, and the outcome is logged where the whole team can see it.",
    Scene: FromConversationScene,
    steps: FROM_CONVERSATION.length,
  },
  {
    id: "from-flow",
    title: "Every location, the same follow-through",
    description:
      "Corporate's flow drops the same check-in on every location's desk at the same lifecycle moment, so the newest hire follows up exactly like the flagship does.",
    Scene: FromFlowScene,
    steps: FROM_FLOW.length,
  },
  {
    id: "from-phone",
    title: "Finished from the studio floor",
    description:
      "The assignment push lands mid-rush, gets snoozed until between classes, and comes back right on time. Call made, task checked off, all from a phone.",
    Scene: FromPhoneScene,
    steps: FROM_PHONE.length,
  },
];

export function TasksUseCases() {
  return (
    <FeatureUseCases
      title={
        <>
          From to-do to done.
          <span className="text-muted-foreground">
            {" "}
            See how gyms and studios keep every promise made in a conversation.
          </span>
        </>
      }
      cases={CASES}
    />
  );
}
